import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Header from "../../Common/Layout/Header";
import MarketingTabs from "../AllTabs/MarketingTabs";
import { useNavigate } from "react-router-dom";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { RiCalendarLine } from "react-icons/ri";

const LeadReports = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [staffFilter, setStaffFilter] = useState("all");
  const [staffList, setStaffList] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    totalLeads: 0,
    newLeads: 0,
    followUpLeads: 0,
    closedLeads: 0,
    repliedLeads: 0,
  });
  const [leadsList, setLeadsList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showMeetingModal, setShowMeetingModal] = useState(false);

  // Custom input component for date picker (same as in your other components)
  const CustomDateInput = React.forwardRef(({ value, onClick }, ref) => (
    <div className="input-group">
      <input
        type="text"
        className="form-control"
        value={value}
        onClick={onClick}
        ref={ref}
        readOnly
        placeholder="Select date"
      />
      <button className="btn btn-secondary" type="button" onClick={onClick}>
        <RiCalendarLine />
      </button>
    </div>
  ));

  const formatDate = (date) => {
    return moment(date).format("YYYY-MM-DD");
  };

  // Set default dates to current month on component mount
  useEffect(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0
    );

    setFromDate(firstDayOfMonth);
    setToDate(lastDayOfMonth);
  }, []);

  // Fetch staff list when component loads
  useEffect(() => {
    if (user?.cid) {
      GetStaffList();
    }
  }, [user]);

  // Fetch data when component loads or filters change
  useEffect(() => {
    if (fromDate && toDate && user?.cid) {
      GetData();
    }
  }, [fromDate, toDate, staffFilter, user]);

  // Fetch staff list from backend
  const GetStaffList = useCallback(async () => {
    if (!user?.cid) return;

    try {
      const url = `${config.GETSTAFFBYCOMPANY}/${user.cid}`;
      const response = await apiService.getAll(url);
      setStaffList(response || []);
    } catch (error) {
      console.error("Error fetching staff list:", error);
      setStaffList([]);
    }
  }, [user]);

  // Fetch data from backend
  const GetData = useCallback(async () => {
    if (!user?.cid) return;

    setIsLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();

      // Convert Date objects to string format for API
      const fromDateStr =
        fromDate instanceof Date ? formatDate(fromDate) : fromDate;
      const toDateStr = toDate instanceof Date ? formatDate(toDate) : toDate;

      if (fromDateStr) params.append("fromDate", fromDateStr);
      if (toDateStr) params.append("toDate", toDateStr);
      if (staffFilter !== "all") params.append("userId", staffFilter);
      params.append("companyId", user.cid);

      // API call to get lead reports data
      const url = `${config.FULLLEADREPORT}?${params}`;
      const response = await apiService.getAll(url);

      // Process the API response - note the response structure has "leads" not "Leads"
      const leads = response?.leads || [];
      const summary = response?.statusSummary || [];

      // Filter leads by staff if a specific staff is selected
      let filteredLeads = leads;
      if (staffFilter !== "all") {
        filteredLeads = leads.filter((lead) => lead.userId === staffFilter);
      }

      // Set dashboard data based on API response
      const totalLeads = filteredLeads.length;
      const newLeads = filteredLeads.filter(
        (lead) => lead.leadStatusName === "NEW"
      ).length;
      const followUpLeads = filteredLeads.filter(
        (lead) => lead.leadStatusName === "FOLLOW UP"
      ).length;
      const closedLeads = filteredLeads.filter(
        (lead) => lead.leadStatusName === "CLOSED"
      ).length;
      const repliedLeads = filteredLeads.filter(
        (lead) => lead.leadStatusName === "REPLIED"
      ).length;

      setDashboardData({
        totalLeads,
        newLeads,
        followUpLeads,
        closedLeads,
        repliedLeads,
      });

      // Transform leads for display
      const transformedLeads = filteredLeads.map((lead) => ({
        id: lead.leadId,
        name: lead.name,
        email: lead.email,
        phone: lead.phoneNumber,
        source: lead.sourceName,
        status: lead.leadStatusName,
        assignedTo: lead.userName,
        createdDate: lead.createdDate,
        meetingComments: lead.meetingComments || [],
        // Add additional fields for the modal
        enquiryType: lead.enquiryType,
        description: lead.description,
        callBack: lead.callBack,
        referenceId: lead.referenceId,
        updatedDate: lead.updatedDate,
        userId: lead.userId,
      }));

      setLeadsList(transformedLeads);
    } catch (error) {
      console.error("Error fetching lead reports data:", error);
      setDashboardData({
        totalLeads: 0,
        newLeads: 0,
        followUpLeads: 0,
        closedLeads: 0,
        repliedLeads: 0,
      });
      setLeadsList([]);
    } finally {
      setIsLoading(false);
    }
  }, [user, fromDate, toDate, staffFilter]);

  // Reset filters
  const resetFilters = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth() + 1,
      0
    );

    setFromDate(firstDayOfMonth);
    setToDate(lastDayOfMonth);
    setStaffFilter("all");
  };

  // Handle date changes for DatePicker
  const handleFromDateChange = (date) => {
    setFromDate(date);
  };

  const handleToDateChange = (date) => {
    setToDate(date);
  };

  // Show meeting details for a lead
  const showMeetings = (lead) => {
    setSelectedLead(lead);
    setShowMeetingModal(true);
  };

  // Close meeting modal
  const closeMeetingModal = () => {
    setShowMeetingModal(false);
    setSelectedLead(null);
  };

  // Define color schemes for status cards
  const statusColors = {
    NEW: {
      background: "linear-gradient(135deg, #4361ee, #3a56d4)",
      icon: "#ffffff",
    },
    "FOLLOW UP": {
      background: "linear-gradient(135deg, #f9c74f, #f4b400)",
      icon: "#ffffff",
    },
    CLOSED: {
      background: "linear-gradient(135deg, #dc2626, #b91c1c)",
      icon: "#ffffff",
    },
    REPLIED: {
      background: "linear-gradient(135deg, #10b981, #0da271)",
      icon: "#ffffff",
    },
  };

  // SVG Icons
  const PlusCircleIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10"></circle>
      <line x1="12" y1="8" x2="12" y2="16"></line>
      <line x1="8" y1="12" x2="16" y2="12"></line>
    </svg>
  );

  const CheckCircleIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
      <polyline points="22 4 12 14.01 9 11.01"></polyline>
    </svg>
  );

  const SyncIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M23 4v6h-6"></path>
      <path d="M1 20v-6h6"></path>
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
      <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
    </svg>
  );

  const LockIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
    </svg>
  );

  const ChatIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="currentColor"
      className="bi bi-chat-dots"
      viewBox="0 0 16 16"
    >
      <path d="M5 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2z" />
      <path d="M2.165 15.803l.02-.004c1.83-.363 2.948-.842 3.468-1.105A9.06 9.06 0 0 0 8 15c4.418 0 8-3.134 8-7s-3.582-7-8-7-8 3.134-8 7c0 1.76.743 3.37 1.97 4.6a10.437 10.437 0 0 1-.524 2.318l-.003.011a10.722 10.722 0 0 1-.244.637c-.079.186.074.394.273.362a21.673 21.673 0 0 0 .693-.125zm.8-3.108a1 1 0 0 0-.287-.801C1.618 10.83 1 9.468 1 8c0-3.192 3.004-6 7-6s7 2.808 7 6c0 3.193-3.004 6-7 6a8.06 8.06 0 0 1-2.088-.272 1 1 0 0 0-.711.074c-.387.196-1.24.57-2.634.893a10.97 10.97 0 0 0 .398-2z" />
    </svg>
  );

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
    return null;
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  <MarketingTabs pageName="LEADREPORTS" />
                </div>
                <div className="card-body">
                  {/* Filter Section */}
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="card bg-light">
                        <div className="card-body p-3">
                          <div className="row g-3 align-items-end">
                            {/* Date Range */}
                            <div className="col-md-4">
                              <div className="row g-2">
                                <div className="col-6">
                                  <label className="form-label small">
                                    From Date
                                  </label>
                                  <DatePicker
                                    selected={fromDate}
                                    onChange={handleFromDateChange}
                                    customInput={<CustomDateInput />}
                                    dateFormat="dd-MMM-yyyy"
                                    showYearDropdown
                                    showMonthDropdown
                                    dropdownMode="select"
                                    yearDropdownItemNumber={50}
                                    scrollableYearDropdown
                                    className="form-control"
                                  />
                                </div>
                                <div className="col-6">
                                  <label className="form-label small">
                                    To Date
                                  </label>
                                  <DatePicker
                                    selected={toDate}
                                    onChange={handleToDateChange}
                                    customInput={<CustomDateInput />}
                                    dateFormat="dd-MMM-yyyy"
                                    showYearDropdown
                                    showMonthDropdown
                                    dropdownMode="select"
                                    yearDropdownItemNumber={50}
                                    scrollableYearDropdown
                                    className="form-control"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Staff Filter */}
                            <div className="col-md-2">
                              <label
                                htmlFor="staffFilter"
                                className="form-label fw-semibold"
                              >
                                Staff
                              </label>
                              <select
                                className="form-select form-select-sm"
                                value={staffFilter}
                                onChange={(e) => setStaffFilter(e.target.value)}
                                style={{ height: "38px" }}
                              >
                                <option value="all">All Staff</option>
                                {staffList.map((staff) => (
                                  <option
                                    key={staff.userId}
                                    value={staff.userId}
                                  >
                                    {staff.name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            {/* Buttons */}
                            <div className="col-md-2">
                              <div className="d-flex gap-2 mt-4">
                                <button
                                  className="btn btn-primary btn-sm w-100"
                                  onClick={GetData}
                                  disabled={isLoading}
                                >
                                  {isLoading ? "Loading..." : "Apply"}
                                </button>
                                <button
                                  className="btn btn-danger btn-sm w-100"
                                  onClick={resetFilters}
                                  style={{ height: "38px" }}
                                >
                                  Reset
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status Cards */}
                  <div className="row mb-4">
                    <div className="col-12">
                      <div className="card bg-white">
                        <div className="card-body bg-light">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0 text-dark">
                              Overview
                            </h5>
                            <span className="badge bg-primary fs-6">
                              Total Leads: {dashboardData.totalLeads}
                            </span>
                          </div>
                          <div className="row">
                            {/* New Leads Card */}
                            <div className="col-md-3 col-lg-3 mb-3">
                              <div
                                className="card text-white border-0 shadow-sm h-100"
                                style={{
                                  background: statusColors.NEW.background,
                                  borderRadius: "15px",
                                  overflow: "hidden",
                                  transition:
                                    "transform 0.3s ease, box-shadow 0.3s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "translateY(-8px) scale(1.03)";
                                  e.currentTarget.style.boxShadow =
                                    "0 8px 20px rgba(0,0,0,0.3)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform =
                                    "translateY(0) scale(1)";
                                  e.currentTarget.style.boxShadow =
                                    "0 4px 12px rgba(0,0,0,0.2)";
                                }}
                              >
                                <div className="card-body d-flex justify-content-between align-items-center p-3">
                                  <div className="text-start">
                                    <p className="mb-1 fw-semibold text-uppercase small text-white">
                                      NEW
                                    </p>
                                    <h2
                                      className="mb-0 fw-bold text-white"
                                      style={{ fontSize: "2.5rem" }}
                                    >
                                      {dashboardData.newLeads}
                                    </h2>
                                  </div>
                                  <div
                                    className="status-icon fs-1"
                                    style={{ opacity: 0.9 }}
                                  >
                                    <PlusCircleIcon />
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Follow Up Card */}
                            <div className="col-md-3 col-lg-3 mb-3">
                              <div
                                className="card text-white border-0 shadow-sm h-100"
                                style={{
                                  background:
                                    statusColors["FOLLOW UP"].background,
                                  borderRadius: "15px",
                                  overflow: "hidden",
                                  transition:
                                    "transform 0.3s ease, box-shadow 0.3s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "translateY(-8px) scale(1.03)";
                                  e.currentTarget.style.boxShadow =
                                    "0 8px 20px rgba(0,0,0,0.3)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform =
                                    "translateY(0) scale(1)";
                                  e.currentTarget.style.boxShadow =
                                    "0 4px 12px rgba(0,0,0,0.2)";
                                }}
                              >
                                <div className="card-body d-flex justify-content-between align-items-center p-3">
                                  <div className="text-start">
                                    <p className="mb-1 fw-semibold text-uppercase small text-white">
                                      FOLLOW UP
                                    </p>
                                    <h2
                                      className="mb-0 fw-bold text-white"
                                      style={{ fontSize: "2.5rem" }}
                                    >
                                      {dashboardData.followUpLeads}
                                    </h2>
                                  </div>
                                  <div
                                    className="status-icon fs-1"
                                    style={{ opacity: 0.9 }}
                                  >
                                    <SyncIcon />
                                  </div>
                                </div>
                              </div>
                            </div>
                            {/* Replied Card */}
                            <div className="col-md-3 col-lg-3 mb-3">
                              <div
                                className="card text-white border-0 shadow-sm h-100"
                                style={{
                                  background: statusColors.REPLIED.background,
                                  borderRadius: "15px",
                                  overflow: "hidden",
                                  transition:
                                    "transform 0.3s ease, box-shadow 0.3s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "translateY(-8px) scale(1.03)";
                                  e.currentTarget.style.boxShadow =
                                    "0 8px 20px rgba(0,0,0,0.3)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform =
                                    "translateY(0) scale(1)";
                                  e.currentTarget.style.boxShadow =
                                    "0 4px 4px 12px rgba(0,0,0,0.2)";
                                }}
                              >
                                <div className="card-body d-flex justify-content-between align-items-center p-3">
                                  <div className="text-start">
                                    <p className="mb-1 fw-semibold text-uppercase small text-white">
                                      REPLIED
                                    </p>
                                    <h2
                                      className="mb-0 fw-bold text-white"
                                      style={{ fontSize: "2.5rem" }}
                                    >
                                      {dashboardData.repliedLeads}
                                    </h2>
                                  </div>
                                  <div
                                    className="status-icon fs-1"
                                    style={{ opacity: 0.9 }}
                                  >
                                    <CheckCircleIcon />
                                  </div>
                                </div>
                              </div>
                            </div>
                            {/* Closed Card */}
                            <div className="col-md-3 col-lg-3 mb-3">
                              <div
                                className="card text-white border-0 shadow-sm h-100"
                                style={{
                                  background: statusColors.CLOSED.background,
                                  borderRadius: "15px",
                                  overflow: "hidden",
                                  transition:
                                    "transform 0.3s ease, box-shadow 0.3s ease",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform =
                                    "translateY(-8px) scale(1.03)";
                                  e.currentTarget.style.boxShadow =
                                    "0 8px 20px rgba(0,0,0,0.3)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform =
                                    "translateY(0) scale(1)";
                                  e.currentTarget.style.boxShadow =
                                    "0 4px 12px rgba(0,0,0,0.2)";
                                }}
                              >
                                <div className="card-body d-flex justify-content-between align-items-center p-3">
                                  <div className="text-start">
                                    <p className="mb-1 fw-semibold text-uppercase small text-white">
                                      CLOSED
                                    </p>
                                    <h2
                                      className="mb-0 fw-bold text-white"
                                      style={{ fontSize: "2.5rem" }}
                                    >
                                      {dashboardData.closedLeads}
                                    </h2>
                                  </div>
                                  <div
                                    className="status-icon fs-1"
                                    style={{ opacity: 0.9 }}
                                  >
                                    <LockIcon />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Leads List */}
                  <div className="row">
                    <div className="col-12">
                      <div className="card bg-white border-0 shadow-sm">
                        <div className="card-body bg-light">
                          <h5 className="card-title text-dark mb-4">
                            Leads List
                          </h5>
                          {isLoading ? (
                            <div className="text-center py-4">
                              Loading leads...
                            </div>
                          ) : leadsList.length > 0 ? (
                            <div className="table-responsive">
                              <table className="table table-hover">
                                <thead>
                                  <tr>
                                    <th scope="col">S.No</th>
                                    <th scope="col">Name</th>
                                    <th scope="col">Email</th>
                                    <th scope="col">Phone</th>
                                    <th scope="col">Source</th>
                                    <th scope="col">Status</th>
                                    <th scope="col">Assigned To</th>
                                    <th scope="col">Created Date</th>
                                    <th scope="col">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {leadsList.map((lead, index) => (
                                    <tr key={index}>
                                      <th scope="row">{index + 1}</th>
                                      <td>{lead.name}</td>
                                      <td>{lead.email}</td>
                                      <td>{lead.phone}</td>
                                      <td>
                                        <span className="badge bg-info">
                                          {lead.source}
                                        </span>
                                      </td>
                                      <td>
                                        <span
                                          className={`badge ${
                                            lead.status === "NEW"
                                              ? "bg-primary"
                                              : lead.status === "FOLLOW UP"
                                              ? "bg-warning"
                                              : lead.status === "CLOSED"
                                              ? "bg-success"
                                              : lead.status === "REPLIED"
                                              ? "bg-info"
                                              : "bg-danger"
                                          }`}
                                        >
                                          {lead.status}
                                        </span>
                                      </td>
                                      <td>{lead.assignedTo || "Unassigned"}</td>
                                      <td>
                                        {moment
                                          .utc(lead.createdDate)
                                          .local()
                                          .format("DD MMM YYYY, hh:mm A")}
                                      </td>
                                      <td>
                                        <button
                                          className="btn btn-sm btn-primary"
                                          onClick={() => showMeetings(lead)}
                                          title="View Meetings"
                                        >
                                          <ChatIcon />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="text-center py-4">
                              No leads found for the selected filters
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Meeting Modal - Large with 4 fields per row */}
      {showMeetingModal && selectedLead && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Lead Details & Meetings</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeMeetingModal}
                ></button>
              </div>
              <div className="modal-body">
                {/* Lead Information - 4 fields per row */}
                <div className="row mb-4">
                  <div className="col-12">
                    {/* Row 1 */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <p className="mb-2">
                          <strong>Name:</strong>
                          <br />
                          <span className="text-dark">{selectedLead.name}</span>
                        </p>
                      </div>
                      <div className="col-md-3">
                        <p className="mb-2">
                          <strong>Email:</strong>
                          <br />
                          <span className="text-dark">
                            {selectedLead.email || "N/A"}
                          </span>
                        </p>
                      </div>
                      <div className="col-md-3">
                        <p className="mb-2">
                          <strong>Phone:</strong>
                          <br />
                          <span className="text-dark">
                            {selectedLead.phone}
                          </span>
                        </p>
                      </div>
                      <div className="col-md-3">
                        <p className="mb-2">
                          <strong>Enquiry Type:</strong>
                          <br />
                          <span className="text-dark">
                            {selectedLead.enquiryType}
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Row 2 */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <p className="mb-2">
                          <strong>Source:</strong>
                          <br />
                          <span className="text-dark">
                            {selectedLead.source}
                          </span>
                        </p>
                      </div>
                      <div className="col-md-3">
                        <p className="mb-2">
                          <strong>Status:</strong>
                          <br />
                          <span
                            className={`badge ${
                              selectedLead.status === "NEW"
                                ? "bg-primary"
                                : selectedLead.status === "FOLLOW UP"
                                ? "bg-warning"
                                : selectedLead.status === "CLOSED"
                                ? "bg-success"
                                : selectedLead.status === "REPLIED"
                                ? "bg-purple"
                                : "bg-secondary"
                            }`}
                          >
                            {selectedLead.status}
                          </span>
                        </p>
                      </div>
                      <div className="col-md-3">
                        <p className="mb-2">
                          <strong>Assigned To:</strong>
                          <br />
                          <span className="text-dark">
                            {selectedLead.assignedTo || "Unassigned"}
                          </span>
                        </p>
                      </div>
                      <div className="col-md-3">
                        <p className="mb-2">
                          <strong>Created Date:</strong>
                          <br />
                          <span className="text-dark">
                            {moment
                              .utc(selectedLead.createdDate)
                              .local()
                              .format("DD MMM YYYY, hh:mm A")}
                          </span>
                        </p>
                      </div>
                    </div>

                    {/* Row 3 */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <p className="mb-2">
                          <strong>Last Updated:</strong>
                          <br />
                          <span className="text-dark">
                            {moment
                              .utc(selectedLead.updatedDate)
                              .local()
                              .format("DD MMM YYYY, hh:mm A")}
                          </span>
                        </p>
                      </div>
                      {selectedLead.callBack && (
                        <div className="col-md-3">
                          <p className="mb-2">
                            <strong>Callback:</strong>
                            <br />
                            <span className="text-dark">
                              {moment
                                .utc(selectedLead.callBack)
                                .local()
                                .format("DD MMM YYYY, hh:mm A")}
                            </span>
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Description with word wrap */}
                    {selectedLead.description && (
                      <div className="row mt-3">
                        <div className="col-12">
                          <p className="mb-2">
                            <strong>Description:</strong>
                          </p>
                          <div
                            className="p-3 bg-light rounded text-wrap border"
                            style={{
                              wordWrap: "break-word",
                              overflowWrap: "break-word",
                              whiteSpace: "normal",
                              minHeight: "60px",
                            }}
                          >
                            {selectedLead.description}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Meetings List with scroll */}

                <div
                  className="border rounded p-3 bg-light"
                  style={{
                    maxHeight: "300px",
                    overflowY: "auto",
                    minHeight: "100px",
                  }}
                >
                  {selectedLead.meetingComments &&
                  selectedLead.meetingComments.length > 0 ? (
                    selectedLead.meetingComments.map((meeting, index) => (
                      <div key={index} className="card mb-2">
                        <div className="card-body p-3">
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <h6 className="card-title mb-0">
                              {meeting.commentedByUserName || "System"}
                            </h6>
                            <small className="text-muted text-end">
                              {moment
                                .utc(meeting.meetingTime)
                                .local()
                                .format("DD MMM YYYY, hh:mm A")}
                            </small>
                          </div>
                          <p
                            className="card-text mb-0 text-wrap"
                            style={{
                              wordWrap: "break-word",
                              overflowWrap: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            {meeting.comment}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-muted my-4">
                      No meetings found for this lead
                    </p>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={closeMeetingModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadReports;
